import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import styled from 'styled-components';
import { getLabDataByName, updateLabData } from '../services/labService';
import { getMemberLabData } from '../services/memberLabService';
import { fetchLabDataByNameFallbacks as sharedFetchLabDataByNameFallbacks } from '../utils/labRecordUtils';
import BloodSugarFormula from '../formula/FbsFpg';
import Hba1cFormula from '../formula/Hba1c';
import MainHeader from '../Components/MainHeader';

const BloodSugarContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const Header = styled.div`
  background-color: #00BBD3;
  padding: 16px 24px;
  color: white;
  font-size: 18px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const TitleSection = styled.div`
  margin-bottom: 20px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #B9B9B9;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  margin-bottom: 8px;
  display: block;
  font-family: 'Noto Sans Thai', sans-serif !important;
  transition: all 0.2s ease;

  &:hover {
    color: #999999;
    transform: translateX(-2px);
  }

  &:active {
    transform: translateX(-1px);
  }
`;

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: bold;
  color: #333333;
  margin: 0 0 8px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const DateText = styled.p`
  font-size: 14px;
  color: #666666;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusCard = styled.div`
  background-color: ${props => {
    switch(props.statusColor) {
      case 'green': return '#34A853';
      case 'red': return '#ff4757';
      case 'gray': return '#e0e0e0';
      default: return '#e0e0e0';
    }
  }};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
`;

const StatusIcon = styled.div`
  width: 88px;
  height: 88px;
  background-color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatusIconImage = styled.img`
  width: 81px;
  height: 81px;
  object-fit: contain;
`;

const StatusContent = styled.div`
  flex: 1;
  color: white;
`;

const StatusTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: white;
  margin: 0 0 4px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusValue = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  margin-bottom: 4px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusLevel = styled.div`
  font-size: 14px;
  color: white;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusDescription = styled.div`
  font-size: 12px;
  color: white;
  line-height: 1.4;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ChartContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const ChartTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 12px;
  color: #333;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ChartWrapper = styled.div`
  width: 100%;
  height: 250px;
  margin-bottom: 16px;
`;

const LegendContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-bottom: 16px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.color};
`;

const TableContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const TableHeader = styled.div`
  background-color: #00BBD3;
  padding: 16px 20px;
  display: flex;
  color: white;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const HeaderColumn = styled.div`
  flex: 1;
  font-size: 16px;
  text-align: center;
  color: white;

  &:first-child {
    text-align: left;
  }
`;

const TableRow = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`;

const TableColumn = styled.div`
  font-size: 14px;
  color: #333333;
  font-family: 'Noto Sans Thai', sans-serif !important;
  text-align: center;

  &:first-child {
    flex: 1;
    text-align: left;
  }

  &:nth-child(2) {
    flex: 1;
    text-align: center;
  }

  &:nth-child(3) {
    flex: 1;
    text-align: center;
  }

  &:last-child {
    flex: 0 0 auto;
    margin-left: 8px;
  }
`;

const StatusBadge = styled.span`
  color: black;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const EditIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 8px;
  width: 24px;
  height: 24px;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  backdrop-filter: blur(2px);

  @keyframes fadeIn {
    from {
      opacity: 0;
      backdrop-filter: blur(0px);
    }
    to {
      opacity: 1;
      backdrop-filter: blur(2px);
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
      backdrop-filter: blur(2px);
    }
    to {
      opacity: 0;
      backdrop-filter: blur(0px);
    }
  }
`;

const Modal = styled.div`
  background-color: white;
  border-radius: 20px;
  width: 100%;
  max-width: 380px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  transform: translateY(0) scale(1);
  animation: modalSlideIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  will-change: transform, opacity;

  @keyframes modalSlideIn {
    0% {
      opacity: 0;
      transform: translateY(30px) scale(0.95);
    }
    50% {
      opacity: 0.8;
      transform: translateY(-5px) scale(1.02);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes modalSlideOut {
    0% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    100% {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
  }
`;

const ModalHeader = styled.div`
  background-color: white;
  color: #333333;
  padding: 24px 24px 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: none;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #333333;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #999999;
  font-size: 28px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  will-change: transform, background-color;

  &:hover {
    background-color: #f5f5f5;
    color: #666666;
    transform: scale(1.1) rotate(90deg);
  }

  &:active {
    transform: scale(0.95) rotate(90deg);
  }
`;

const ModalContent = styled.div`
  padding: 24px;

  & > div {
    animation: slideUp 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    animation-fill-mode: both;
  }

  & > div:nth-child(1) { animation-delay: 0.1s; }
  & > div:nth-child(2) { animation-delay: 0.2s; }
  & > div:nth-child(3) { animation-delay: 0.3s; }
  & > div:nth-child(4) { animation-delay: 0.4s; }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const FormRow = styled.div`
  margin-bottom: 24px;

  &:last-of-type {
    margin-bottom: 32px;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 500;
  color: #333333;
  margin-bottom: 12px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: 1px solid #e1e5e9;
  border-radius: 16px;
  font-size: 18px;
  color: #333333;
  background-color: white;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: 400;
  box-sizing: border-box;
  text-align: left;
  will-change: border-color, box-shadow, transform;

  &:focus {
    outline: none;
    border-color: #00BBD3;
    box-shadow: 0 0 0 4px rgba(0, 187, 211, 0.15), 0 4px 20px rgba(0, 187, 211, 0.1);
    transform: translateY(-1px);
  }

  &:hover {
    border-color: #00BBD3;
    transform: translateY(-0.5px);
  }

  &::placeholder {
    color: #999999;
    font-weight: normal;
  }
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const UnitLabel = styled.span`
  margin-left: 12px;
  color: #999999;
  font-size: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
  flex-shrink: 0;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 32px;
  padding-top: 8px;
`;

const Button = styled.button`
  flex: 1;
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const SaveButton = styled(Button)`
  background-color: #00BBD3;
  color: white;
  border-radius: 25px;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  will-change: transform, box-shadow, background-color;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.6s;
  }

  &:hover {
    background-color: #009bb3;
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 25px rgba(0, 187, 211, 0.3);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px) scale(1.01);
    box-shadow: 0 4px 15px rgba(0, 187, 211, 0.4);
  }
`;

const MonthSelectorContainer = styled.div`
  margin-bottom: 24px;
`;

const MonthSelector = styled.select`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid #e1e5e9;
  border-radius: 16px;
  font-size: 16px;
  color: #333333;
  background-color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23666' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 16px center;
  background-repeat: no-repeat;
  background-size: 20px;
  padding-right: 48px;

  &:hover {
    border-color: #00BBD3;
    box-shadow: 0 2px 8px rgba(0, 187, 211, 0.1);
  }

  &:focus {
    outline: none;
    border-color: #00BBD3;
    box-shadow: 0 0 0 4px rgba(0, 187, 211, 0.15);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 20px;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #00BBD3;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const FadeInContainer = styled.div`
  animation: fadeIn 0.5s ease-in;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
  margin-top: 16px;
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.$disabled ? '#e0e0e0' : '#00BBD3'};
  background-color: ${props => props.$disabled ? '#f5f5f5' : props.$active ? '#00BBD3' : 'white'};
  color: ${props => props.$disabled ? '#999' : props.$active ? 'white' : '#00BBD3'};
  border-radius: 8px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  transition: all 0.2s ease;
  font-family: 'Noto Sans Thai', sans-serif !important;
  min-width: 40px;

  &:hover {
    background-color: ${props => props.$disabled ? '#f5f5f5' : props.$active ? '#00BBD3' : '#e6f7f9'};
    transform: ${props => props.$disabled ? 'none' : 'translateY(-2px)'};
  }

  &:active {
    transform: ${props => props.$disabled ? 'none' : 'translateY(0)'};
  }
`;

const PageInfo = styled.div`
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  padding: 0 8px;
`;


const BloodSugarPage = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [allData, setAllData] = useState([]);
  const [fbsData, setFbsData] = useState([]);
  const [hba1cData, setHba1cData] = useState([]);
  const [eagData, setEagData] = useState([]);
  const [nonNpoData, setNonNpoData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [dateRange, setDateRange] = useState('');
  const [latestStatus, setLatestStatus] = useState(null);

  const getEagStatus = (value) => {
    const numericValue = parseFloat(value);
    if (!Number.isFinite(numericValue)) return null;
    const statusText = numericValue < 70 ? 'ต่ำ' : numericValue > 154 ? 'สูง' : 'ปกติ';
    return {
      title: statusText === 'ปกติ' ? 'น้ำตาลเฉลี่ยปกติ' : `น้ำตาลเฉลี่ย${statusText}`,
      color: statusText === 'ปกติ' ? 'green' : 'red',
      value: `${numericValue.toFixed(0)} mg/dL`,
      status: statusText,
      rawValue: numericValue
    };
  };

  // Load blood sugar data from API
  const loadBloodSugarData = async () => {
    try {
      setLoading(true);

      const [fastingResponse, nonNpoResponse, hba1cResponse, eagResponse] = await Promise.all([
        sharedFetchLabDataByNameFallbacks(getLabDataByName, [
          'sugar_fasting',
          'glucoseresult',
          'fbs',
          'fbs/fpg',
          'fpg',
          'sugar'
        ]),
        sharedFetchLabDataByNameFallbacks(getLabDataByName, [
          'sugar_non_npo',
          'non_npo_glucose',
          'glucosenonnpc',
          'glucosenonnpcresult',
          'glucose_nonnpc',
          'glucose_nonnpcresult',
          'glucose_non_npc',
          'glucose_non_npcresult',
          'nonnpobloodglucose',
          'nonnpo',
          'diabetesnonnpo',
          'nonnpoglucose',
          'randombloodsugar',
          'rbs'
        ]),
        sharedFetchLabDataByNameFallbacks(getLabDataByName, [
          'hba1c_daily',
          'diabeteshba1c',
          'hba1c',
          'hba1chba1c',
          'hba1chba1cresult'
        ]),
        sharedFetchLabDataByNameFallbacks(getLabDataByName, [
          'eag',
          'hba1ceag',
          'hba1ceagresult',
          'hba1c eag result',
          'hba1c eag'
        ])
      ]);

      let fastingArray = fastingResponse || [];
      let nonNpoArray = nonNpoResponse || [];
      let hba1cArray = hba1cResponse || [];
      let eagArray = eagResponse || [];

      const convertToDbFormat = (items) =>
        (items || []).map(item => ({ value: item.value, dateadd: item.date, uploader: '' }));

      const mergeUniqueLabItems = (baseItems, extraItems) => {
        const seen = new Set((baseItems || []).map(item => `${item.dateadd || ''}|${item.value ?? ''}`));
        const merged = [...(baseItems || [])];
        (extraItems || []).forEach(item => {
          const key = `${item.dateadd || ''}|${item.value ?? ''}`;
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(item);
          }
        });
        return merged.sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));
      };

      const getMemberLabItems = (labData, keys) => {
        const collected = [];
        keys.forEach((key) => {
          if (labData?.[key]?.length > 0) {
            collected.push(...labData[key]);
          }
        });
        return collected;
      };

      if (fastingArray.length === 0 || nonNpoArray.length === 0 || hba1cArray.length === 0 || eagArray.length === 0) {
        try {
          const memberLabData = await getMemberLabData();
          const labData = memberLabData?.labData;
          if (labData) {
            const fbsFallback = convertToDbFormat(
              getMemberLabItems(labData, ['sugar_fasting', 'glucoseresult', 'fbs', 'fbs/fpg', 'fpg', 'sugar'])
            );
            const nonNpoFallback = convertToDbFormat(
              getMemberLabItems(labData, [
                'sugar_non_npo',
                'non_npo_glucose',
                'glucosenonnpc',
                'glucosenonnpcresult',
                'glucose_nonnpc',
                'glucose_nonnpcresult',
                'glucose_non_npc',
                'glucose_non_npcresult',
                'nonnpobloodglucose',
                'nonnpo',
                'diabetesnonnpo',
                'nonnpoglucose',
                'randombloodsugar',
                'rbs'
              ])
            );
            const hba1cFallback = convertToDbFormat(
              getMemberLabItems(labData, ['hba1c_daily', 'diabeteshba1c', 'hba1c', 'hba1chba1c', 'hba1chba1cresult'])
            );
            const eagFallback = convertToDbFormat(
              getMemberLabItems(labData, ['eag', 'hba1ceag', 'hba1ceagresult', 'hba1c eag result', 'hba1c eag'])
            );

            fastingArray = mergeUniqueLabItems(fastingArray, fbsFallback);
            nonNpoArray = mergeUniqueLabItems(nonNpoArray, nonNpoFallback);
            hba1cArray = mergeUniqueLabItems(hba1cArray, hba1cFallback);
            eagArray = mergeUniqueLabItems(eagArray, eagFallback);
          }
        } catch (e) {
        }
      }

      // Sort descending (newest first)
      fastingArray.sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));
      nonNpoArray.sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));
      hba1cArray.sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));
      eagArray.sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));

      // Combine all records by date (recordsMap pattern)
      const recordsMap = new Map();

      fastingArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, fasting: null, nonNpo: null, hba1c: null, eag: null });
        }
        // เก็บแค่ค่าแรก (sort desc = ล่าสุด) ไม่ overwrite
        if (recordsMap.get(date).fasting === null) recordsMap.get(date).fasting = item.value;
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = item.dateadd;
      });

      nonNpoArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, fasting: null, nonNpo: null, hba1c: null, eag: null });
        }
        if (recordsMap.get(date).nonNpo === null) recordsMap.get(date).nonNpo = item.value;
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = item.dateadd;
      });

      hba1cArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, fasting: null, nonNpo: null, hba1c: null, eag: null });
        }
        if (recordsMap.get(date).hba1c === null) recordsMap.get(date).hba1c = item.value;
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = item.dateadd;
      });

      eagArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, fasting: null, nonNpo: null, hba1c: null, eag: null });
        }
        if (recordsMap.get(date).eag === null) recordsMap.get(date).eag = item.value;
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = item.dateadd;
      });

      // Convert to array and sort by date (newest first)
      const allRecords = Array.from(recordsMap.values()).sort((a, b) => {
        const dateA = new Date(a.rawDate).getTime();
        const dateB = new Date(b.rawDate).getTime();
        return dateB - dateA;
      });

      // Process data for individual charts from deduped allRecords
      const processedFasting = allRecords
        .filter(record => record.fasting !== null && record.fasting !== undefined && record.fasting !== '')
        .sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate))
        .map(record => {
          const date = new Date(record.rawDate);
          const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
          return {
            date: formattedDate,
            value: record.fasting != null ? parseFloat(record.fasting) || 0 : null,
            rawDate: record.rawDate
          };
        });

      const processedNonNpo = allRecords
        .filter(record => record.nonNpo !== null && record.nonNpo !== undefined && record.nonNpo !== '')
        .sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate))
        .map(record => {
          const date = new Date(record.rawDate);
          const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
          return {
            date: formattedDate,
            value: record.nonNpo != null ? parseFloat(record.nonNpo) || 0 : null,
            rawDate: record.rawDate
          };
        });

      const processedHbA1c = allRecords
        .filter(record => record.hba1c !== null && record.hba1c !== undefined && record.hba1c !== '')
        .sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate))
        .map(record => {
          const date = new Date(record.rawDate);
          const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
          return {
            date: formattedDate,
            value: record.hba1c != null ? parseFloat(record.hba1c) || 0 : null,
          rawDate: record.rawDate
        };
      });

      const processedEag = allRecords
        .filter(record => record.eag !== null && record.eag !== undefined && record.eag !== '')
        .sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate))
        .map(record => {
          const date = new Date(record.rawDate);
          const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
          return {
            date: formattedDate,
            value: record.eag != null ? parseFloat(record.eag) || 0 : null,
            rawDate: record.rawDate
          };
        });

      setFbsData(processedFasting);
      setNonNpoData(processedNonNpo);
      setHba1cData(processedHbA1c);
      setEagData(processedEag);
      setAllData(allRecords);

      if (allRecords.length > 0) {
        const latestRecord = allRecords[0];
        const latestMeasurement =
          latestRecord.fasting != null && latestRecord.fasting !== ''
            ? { value: latestRecord.fasting, unit: 'mg/dL' }
            : latestRecord.nonNpo != null && latestRecord.nonNpo !== ''
              ? { value: latestRecord.nonNpo, unit: 'mg/dL' }
              : latestRecord.hba1c != null && latestRecord.hba1c !== ''
                ? { value: latestRecord.hba1c, unit: '%' }
                : null;

        if (latestMeasurement?.value != null) {
          const latestFieldStatuses = [];
          if (latestRecord.fasting != null && latestRecord.fasting !== '') {
            latestFieldStatuses.push(BloodSugarFormula(latestRecord.fasting));
          }
          if (latestRecord.nonNpo != null && latestRecord.nonNpo !== '') {
            latestFieldStatuses.push(BloodSugarFormula(latestRecord.nonNpo));
          }
          if (latestRecord.hba1c != null && latestRecord.hba1c !== '') {
            latestFieldStatuses.push(Hba1cFormula(latestRecord.hba1c));
          }
          if (latestRecord.eag != null && latestRecord.eag !== '') {
            const eagStatus = getEagStatus(latestRecord.eag);
            if (eagStatus) latestFieldStatuses.push(eagStatus);
          }

          setLatestStatus(
            latestFieldStatuses.find(item => item?.color === 'red')
            || latestFieldStatuses.find(item => item?.color === 'green')
            || latestFieldStatuses[0]
            || null
          );
        }

      } else {
        setAllData([]);
        setTableData([]);
        setDateRange('ยังไม่มีข้อมูล');
        setLatestStatus(null);
      }
    } catch (error) {
        setAllData([]);
        setTableData([]);
        setDateRange('ยังไม่มีข้อมูล');
        setLatestStatus(null);
      } finally {
        setLoading(false);
      }
  };

  useEffect(() => {
    loadBloodSugarData();
  }, []);

  // Transform allData for chart and table
  useEffect(() => {
    if (allData.length === 0) {
      return;
    }

    // Transform data for table (same pattern as KidneyPage/LipidPage)
    const formatDisplayDate = (dateStr) => {
      const date = new Date(dateStr);
      return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear().toString().slice(-2)}`;
    };

    const calculateRisk = (fasting, nonNpo, hba1c, eag) => {
      let hasRed = false;

      if (fasting) {
        const result = BloodSugarFormula(fasting);
        if (result && result.color === 'red') hasRed = true;
      }
      if (nonNpo) {
        const result = BloodSugarFormula(nonNpo);
        if (result && result.color === 'red') hasRed = true;
      }
      if (hba1c) {
        const result = Hba1cFormula(hba1c);
        if (result && result.color === 'red') hasRed = true;
      }
      if (eag) {
        const result = getEagStatus(eag);
        if (result && result.color === 'red') hasRed = true;
      }

      return hasRed ? 'ผิดปกติ' : 'ปกติ';
    };

    const tableRecords = allData.map(record => ({
      ...record,
      displayDate: formatDisplayDate(record.date),
      risk: calculateRisk(record.fasting, record.nonNpo, record.hba1c, record.eag)
    }));

    setTableData(tableRecords);

    // Transform data for chart - แยกแสดงทั้ง 3 เส้น (Fasting, Non-NPO, HbA1c)
    const chartRecords = allData
      .sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate))
      .map(record => {
        const date = new Date(record.rawDate);
        const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;

        return {
          date: formattedDate,
          fasting: record.fasting ? parseFloat(record.fasting) : null,
          nonNpo: record.nonNpo ? parseFloat(record.nonNpo) : null,
          hba1c: record.hba1c ? parseFloat(record.hba1c) : null
        };
      });

    void chartRecords;

    // Calculate date range for display
    const dates = allData.map(item => new Date(item.rawDate));
    const oldestDate = new Date(Math.min(...dates));
    const newestDate = new Date(Math.max(...dates));

    const thaiMonths = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
                        'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];

    const formatThaiDate = (date) => {
      const day = date.getDate();
      const month = thaiMonths[date.getMonth()];
      const year = date.getFullYear() + 543;
      return `${day} ${month} ${year}`;
    };

    if (allData.length === 1) {
      setDateRange(`ผลตรวจวันที่ ${formatThaiDate(newestDate)}`);
    } else {
      setDateRange(`ผลตรวจวันที่ ${formatThaiDate(oldestDate)} - ${formatThaiDate(newestDate)}`);
    }
  }, [allData]);

  const handleBackClick = () => {
    navigate('/dashboard');
  };

  const handleEditClick = (item, index) => {
    setEditingData({
      ...item,
      index
    });
    setShowModal(true);
  };

  const handleModalClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowModal(false);
      setEditingData(null);
      setIsClosing(false);
    }, 250);
  };

  const handleSave = async () => {
    try {

      // Add a subtle success animation
      const button = document.querySelector('button[type="submit"]');
      if (button) {
        button.style.transform = 'scale(0.95)';
        setTimeout(() => {
          button.style.transform = 'scale(1)';
        }, 150);
      }

      // Update all 3 fields (fasting, nonNpo, hba1c)
      const updatePromises = [];

      if (editingData.fasting) {
        updatePromises.push(
          updateLabData(editingData.rawDate, 'sugar_fasting', editingData.fasting, 'm')
        );
      }

      if (editingData.nonNpo) {
        updatePromises.push(
          updateLabData(editingData.rawDate, 'sugar_non_npo', editingData.nonNpo, 'm')
        );
      }

      if (editingData.hba1c) {
        updatePromises.push(
          updateLabData(editingData.rawDate, 'hba1c', editingData.hba1c, 'm')
        );
      }

      if (editingData.eag) {
        updatePromises.push(
          updateLabData(editingData.rawDate, 'eag', editingData.eag, 'm')
        );
      }

      await Promise.all(updatePromises);

      alert('บันทึกข้อมูลเรียบร้อยแล้ว');
      handleModalClose();

      // Reload data
      loadBloodSugarData();
    } catch (error) {
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง');
    }
  };

  return (
    <BloodSugarContainer>
      <MainHeader />

      <Header>
        น้ำตาล
      </Header>

      <Content>
        <TitleSection>
          <BackButton onClick={handleBackClick}>‹ ย้อนกลับ</BackButton>
          <PageTitle style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <StatusIconImage src="./น้ำตาลสี.png" alt="น้ำตาล" style={{ width: "32px", height: "32px" }} />
            ระดับน้ำตาลในเลือด
          </PageTitle>
          <DateText>{dateRange || 'กำลังโหลด...'}</DateText>
        </TitleSection>

        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>กำลังโหลดข้อมูล...</LoadingText>
          </LoadingContainer>
        ) : (
          <>
            {/* Status Card — comment ทั้งการ์ดออก เพราะย้ายไอคอนไปไว้ที่ Title ด้านบนแล้ว */}
            {/* ปิด logic การตัดสินสี/สถานะ (แดง/เขียว/ผิดปกติ) ทั้งหมด — เหลือแค่ไอคอน + ชื่อหมวดเท่านั้น */}
            {/*
            {latestStatus && allData.length > 0 && (
              <FadeInContainer>
                <StatusCard
                  // statusColor={latestStatus.color}
                  statusColor="gray"
                >
                  <StatusIcon>
                    <StatusIconImage src="./น้ำตาลสี.png" alt="Blood Sugar Icon" />
                  </StatusIcon>
                  <StatusContent>
                    <StatusTitle>น้ำตาล</StatusTitle>
                    // <StatusValue>
                      {allData[0]?.fasting != null && allData[0]?.fasting !== ''
                        ? `${allData[0].fasting} mg/dL`
                        : allData[0]?.nonNpo != null && allData[0]?.nonNpo !== ''
                          ? `${allData[0].nonNpo} mg/dL`
                          : allData[0]?.hba1c != null && allData[0]?.hba1c !== ''
                            ? `${allData[0].hba1c} %`
                            : allData[0]?.eag != null && allData[0]?.eag !== ''
                              ? `${allData[0].eag} mg/dL`
                            : '-'}
                    // </StatusValue>
                    // <StatusLevel>{latestStatus.title}</StatusLevel>
                  </StatusContent>
                </StatusCard>
              </FadeInContainer>
            )}
            */}


            {/* FBS Chart */}
            <FadeInContainer>
              <ChartContainer>
                <ChartTitle style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', color: '#333' }}>น้ำตาลในเลือดขณะอดอาหาร (FBS)</ChartTitle>
                <LegendContainer>
                  <LegendItem>
                    <LegendDot color="#FF5722" />
                    <span>ระดับน้ำตาล FBS</span>
                  </LegendItem>
                </LegendContainer>

                <ChartWrapper>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={fbsData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#666666' }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis
                        domain={[0, 200]}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#666666' }}
                        label={{ value: 'mg/dL', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#FF5722"
                        strokeWidth={3}
                        dot={{ fill: '#FF5722', strokeWidth: 2, r: 6 }}
                        activeDot={{ r: 8, fill: '#FF5722' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </ChartContainer>
            </FadeInContainer>

            {/* HbA1c Chart */}
            <FadeInContainer>
              <ChartContainer>
                <ChartTitle style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', color: '#333' }}>น้ำตาลสะสม (HbA1c)</ChartTitle>
                <LegendContainer>
                  <LegendItem>
                    <LegendDot color="#2196F3" />
                    <span>ระดับ HbA1c</span>
                  </LegendItem>
                </LegendContainer>

                <ChartWrapper>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={hba1cData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#666666' }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis
                        domain={[0, 10]}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#666666' }}
                        label={{ value: '%', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#2196F3"
                        strokeWidth={3}
                        dot={{ fill: '#2196F3', strokeWidth: 2, r: 6 }}
                        activeDot={{ r: 8, fill: '#2196F3' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </ChartContainer>
            </FadeInContainer>

            {/* eAG Chart */}
            <FadeInContainer>
              <ChartContainer>
                <ChartTitle style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', color: '#333' }}>eAG (Estimated Average Glucose)</ChartTitle>
                <LegendContainer>
                  <LegendItem>
                    <LegendDot color="#2196F3" />
                    <span>ระดับ eAG</span>
                  </LegendItem>
                </LegendContainer>

                <ChartWrapper>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={eagData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#666666' }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis
                        domain={[0, 300]}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#666666' }}
                        label={{ value: 'mg/dL', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#2196F3"
                        strokeWidth={3}
                        dot={{ fill: '#2196F3', strokeWidth: 2, r: 6 }}
                        activeDot={{ r: 8, fill: '#2196F3' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </ChartContainer>
            </FadeInContainer>

            {/* Non-NPO Chart */}
            <FadeInContainer>
              <ChartContainer>
                <ChartTitle style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', color: '#333' }}>น้ำตาลในเลือดไม่อดอาหาร (Non-NPO)</ChartTitle>
                <LegendContainer>
                  <LegendItem>
                    <LegendDot color="#34A853" />
                    <span>ระดับน้ำตาล Non-NPO</span>
                  </LegendItem>
                </LegendContainer>

                <ChartWrapper>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={nonNpoData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#666666' }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis
                        domain={[0, 200]}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#666666' }}
                        label={{ value: 'mg/dL', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#34A853"
                        strokeWidth={3}
                        dot={{ fill: '#34A853', strokeWidth: 2, r: 6 }}
                        activeDot={{ r: 8, fill: '#34A853' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </ChartContainer>
            </FadeInContainer>

            {tableData.length > 0 && (
              <FadeInContainer>
                <TableContainer>
                  <TableHeader>
                    <HeaderColumn>วันที่ตรวจ</HeaderColumn>
                    <HeaderColumn>ค่าที่วัด</HeaderColumn>
                    <HeaderColumn>ความเสี่ยง</HeaderColumn>
                  </TableHeader>

                  {tableData.map((item, index) => {
                    const values = [];
                    if (item.fasting) values.push(`Fasting: ${item.fasting} mg/dL`);
                    if (item.nonNpo) values.push(`Non-NPO: ${item.nonNpo} mg/dL`);
                    if (item.hba1c) values.push(`HbA1c: ${item.hba1c}%`);
                    if (item.eag) values.push(`eAG: ${item.eag} mg/dL`);

                    return (
                      <TableRow key={index}>
                        <TableColumn>{item.displayDate}</TableColumn>
                        <TableColumn>{values.join(', ') || '-'}</TableColumn>
                        <TableColumn>
                          <StatusBadge type={item.risk === 'ผิดปกติ' ? 'abnormal' : 'normal'}>{item.risk}</StatusBadge>
                        </TableColumn>
                        <TableColumn>
                          {/* ปิดการแก้ไขข้อมูลจากตารางประวัติ — comment ไอคอนแก้ไขไว้ ไม่ลบ */}
                          {/* <EditIcon onClick={() => handleEditClick(item, index)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <g clipPath="url(#clip0_1060_3686)">
                              <path d="M3.5 24H18.5C19.4297 23.9974 20.3204 23.626 20.9765 22.9674C21.6327 22.3087 22.0008 21.4167 22 20.487V12.95C22 12.6848 21.8946 12.4304 21.7071 12.2429C21.5196 12.0554 21.2652 11.95 21 11.95C20.7348 11.95 20.4804 12.0554 20.2929 12.2429C20.1054 12.4304 20 12.6848 20 12.95V20.487C20.0013 20.8864 19.8441 21.2701 19.5629 21.5537C19.2817 21.8374 18.8994 21.9979 18.5 22H3.5C3.10057 21.9979 2.7183 21.8374 2.43708 21.5537C2.15587 21.2701 1.99867 20.8864 2 20.487V5.513C1.99867 5.11357 2.15587 4.72993 2.43708 4.44627C2.7183 4.16262 3.10057 4.00212 3.5 4H11C11.2652 4 11.5196 3.89464 11.7071 3.70711C11.8946 3.51957 12 3.26522 12 3C12 2.73478 11.8946 2.48043 11.7071 2.29289C11.5196 2.10536 11.2652 2 11 2H3.5C2.57031 2.00265 1.67964 2.37403 1.02346 3.03265C0.367281 3.69126 -0.000797091 4.5833 1.29611e-06 5.513V20.487C-0.000797091 21.4167 0.367281 22.3087 1.02346 22.9674C1.67964 23.626 2.57031 23.9974 3.5 24Z" fill="#34A853"/>
                              <path d="M9.45499 10.544L8.66599 14.158C8.63027 14.3219 8.63638 14.4922 8.68373 14.6531C8.73109 14.8141 8.81818 14.9605 8.93699 15.079C9.05742 15.1945 9.20393 15.2792 9.36408 15.326C9.52423 15.3729 9.69331 15.3804 9.85699 15.348L13.463 14.557C13.6502 14.5159 13.8217 14.4218 13.957 14.286L23.071 5.17197C23.3496 4.89339 23.5706 4.56265 23.7214 4.19864C23.8722 3.83463 23.9498 3.44448 23.9498 3.05047C23.9498 2.65647 23.8722 2.26632 23.7214 1.90231C23.5706 1.5383 23.3496 1.20756 23.071 0.928973C22.4998 0.38311 21.7401 0.0784912 20.95 0.0784912C20.1599 0.0784912 19.4002 0.38311 18.829 0.928973L9.72899 10.052C9.59241 10.1863 9.4973 10.3571 9.45499 10.544ZM20.243 2.34397C20.4332 2.16171 20.6865 2.05997 20.95 2.05997C21.2135 2.05997 21.4667 2.16171 21.657 2.34397C21.842 2.5326 21.9456 2.78627 21.9456 3.05047C21.9456 3.31468 21.842 3.56834 21.657 3.75697L20.95 4.46397L19.536 3.04997L20.243 2.34397ZM11.343 11.258L18.117 4.46697L19.517 5.87397L12.74 12.667L10.945 13.061L11.343 11.258Z" fill="#34A853"/>
                            </g>
                            <defs>
                              <clipPath id="clip0_1060_3686">
                                <rect width="24" height="24" fill="white"/>
                              </clipPath>
                            </defs>
                          </svg>
                        </EditIcon> */}
                      </TableColumn>
                    </TableRow>
                    );
                  })}
                </TableContainer>

                {/* Pagination removed as requested */}
              </FadeInContainer>
            )}
          </>
        )}
      </Content>

      {showModal && editingData && (
        <ModalOverlay
          onClick={handleModalClose}
          style={{
            animation: isClosing ? 'fadeOut 0.25s ease-out forwards' : undefined
          }}
        >
          <Modal
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: isClosing ? 'modalSlideOut 0.25s ease-out forwards' : undefined
            }}
          >
            <ModalHeader>
              <ModalTitle>แก้ไขข้อมูลน้ำตาล</ModalTitle>
              <CloseButton onClick={handleModalClose}>×</CloseButton>
            </ModalHeader>
            <ModalContent>
              <FormRow>
                <Label>วันที่ตรวจ {editingData.displayDate}</Label>
              </FormRow>

              <FormRow>
                <Label>น้ำตาลอดอาหาร (Fasting)</Label>
                <InputContainer>
                  <Input
                    type="text"
                    value={editingData.fasting || ''}
                    onChange={(e) => setEditingData({...editingData, fasting: e.target.value})}
                    placeholder="ไม่มีข้อมูล"
                  />
                  <UnitLabel>mg/dL</UnitLabel>
                </InputContainer>
              </FormRow>

              <FormRow>
                <Label>น้ำตาลไม่อดอาหาร (Non-NPO)</Label>
                <InputContainer>
                  <Input
                    type="text"
                    value={editingData.nonNpo || ''}
                    onChange={(e) => setEditingData({...editingData, nonNpo: e.target.value})}
                    placeholder="ไม่มีข้อมูล"
                  />
                  <UnitLabel>mg/dL</UnitLabel>
                </InputContainer>
              </FormRow>

              <FormRow>
                <Label>น้ำตาลสะสม (HbA1c)</Label>
                <InputContainer>
                  <Input
                    type="text"
                    value={editingData.hba1c || ''}
                    onChange={(e) => setEditingData({...editingData, hba1c: e.target.value})}
                    placeholder="ไม่มีข้อมูล"
                  />
                  <UnitLabel>%</UnitLabel>
                </InputContainer>
              </FormRow>

              <FormRow>
                <Label>eAG (Estimated Average Glucose)</Label>
                <InputContainer>
                  <Input
                    type="text"
                    value={editingData.eag || ''}
                    onChange={(e) => setEditingData({...editingData, eag: e.target.value})}
                    placeholder="ไม่มีข้อมูล"
                  />
                  <UnitLabel>mg/dL</UnitLabel>
                </InputContainer>
              </FormRow>

              <ButtonRow>
                <SaveButton onClick={handleSave} type="submit">บันทึก</SaveButton>
              </ButtonRow>
            </ModalContent>
          </Modal>
        </ModalOverlay>
      )}
    </BloodSugarContainer>
  );
};

export default BloodSugarPage;
